import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import PluginEntitiesProvider from './PluginEntitiesProvider';
import { useRelationshipsContext } from './RelationshipsProvider';
import getPluginKey from '../helpers/getPluginKey';

const LinkedEntitiesProvider = ({
  children,
  sourceEntityDomain,
  sourceEntityType,
  plugins,
}) => {
  const { relationshipDataBySource } = useRelationshipsContext();
  const idsByDomainAndType = useMemo(() => {
    const result = {};
    const relationshipsBySourceId =
      relationshipDataBySource[sourceEntityDomain]?.[sourceEntityType] || {};
    const sourceEntityIds = Object.keys(relationshipsBySourceId);
    sourceEntityIds.forEach((sourceEntityId) => {
      const data =
        relationshipDataBySource[sourceEntityDomain]?.[sourceEntityType]?.[
          sourceEntityId
        ];
      if (data?.relationshipsData) {
        data.relationshipsData.forEach(({ domain, type, entityId }) => {
          if (!result[domain]) {
            result[domain] = {};
          }
          if (!result[domain][type]) {
            result[domain][type] = [];
          }
          result[domain][type].push(entityId);
        });
      }
    });
    return result;
  }, [relationshipDataBySource, sourceEntityDomain, sourceEntityType]);

  return plugins.reduce((content, plugin) => {
    return (
      <PluginEntitiesProvider
        plugin={plugin}
        key={getPluginKey(plugin)}
        entityIds={
          idsByDomainAndType?.[plugin.metadata.domain]?.[plugin.metadata.type]
        }
      >
        {content}
      </PluginEntitiesProvider>
    );
  }, children);
};

LinkedEntitiesProvider.displayName = 'LinkedEntitiesProvider';

LinkedEntitiesProvider.propTypes = {
  /**
   * The source entity domain, as used by relationship service.
   */
  sourceEntityDomain: PropTypes.string.isRequired,
  /**
   * The source entity type, as used by relationship service.
   */
  sourceEntityType: PropTypes.string.isRequired,
  /**
   * The content of the provider.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export default LinkedEntitiesProvider;
